import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

const SuccessModal = ({ isOpen, onClose }) => {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      firedRef.current = false;
      return;
    }

    // Only fire once per open
    if (firedRef.current) return;
    firedRef.current = true;

    // Short delay so the modal renders first
    const t = setTimeout(() => {
      // Burst from bottom center
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { x: 0.5, y: 0.85 },
        colors: ['#0ea5e9', '#38bdf8', '#ffffff', '#7dd3fc', '#0284c7'],
        ticks: 200,
        gravity: 0.9,
        scalar: 1.1,
        zIndex: 10000,
      });

      // Second burst slightly delayed for depth
      setTimeout(() => {
        confetti({
          particleCount: 60,
          spread: 120,
          origin: { x: 0.35, y: 0.75 },
          colors: ['#0ea5e9', '#e0f2fe', '#ffffff'],
          ticks: 160,
          gravity: 1,
          scalar: 0.9,
          zIndex: 10000,
        });
        confetti({
          particleCount: 60,
          spread: 120,
          origin: { x: 0.65, y: 0.75 },
          colors: ['#0ea5e9', '#e0f2fe', '#ffffff'],
          ticks: 160,
          gravity: 1,
          scalar: 0.9,
          zIndex: 10000,
        });
      }, 200);
    }, 100);

    return () => clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-[#070707] border border-white/[0.08] rounded-[2.5rem] p-10 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95 duration-500 relative overflow-hidden">

        {/* Glow ring */}
        <div className="absolute inset-0 rounded-[2.5rem] ring-1 ring-sky-500/20 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-sky-500/10 blur-[60px] rounded-full pointer-events-none" />

        {/* Animated checkmark circle */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          {/* Outer pulse ring */}
          <div className="absolute inset-0 rounded-full bg-sky-500/10 animate-ping" style={{ animationDuration: '2s' }} />
          {/* Inner circle */}
          <div className="relative w-24 h-24 bg-sky-500/15 border border-sky-500/30 rounded-full flex items-center justify-center">
            {/* Checkmark SVG with draw animation */}
            <svg
              className="w-10 h-10 text-sky-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ filter: 'drop-shadow(0 0 8px rgba(14,165,233,0.6))' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M5 13l4 4L19 7"
                style={{
                  strokeDasharray: 30,
                  strokeDashoffset: 0,
                  animation: 'draw-check 0.5s ease-out 0.2s both',
                }}
              />
            </svg>
          </div>
        </div>

        {/* CSS for draw animation */}
        <style>{`
          @keyframes draw-check {
            from { stroke-dashoffset: 30; }
            to { stroke-dashoffset: 0; }
          }
        `}</style>

        {/* Headline */}
        <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.5em] mb-3">
          Registration Complete
        </p>
        <h2 className="text-4xl font-black text-white uppercase tracking-tighter mb-1 leading-tight">
          You're In.
        </h2>
        <p className="text-sm font-bold text-sky-400/70 uppercase tracking-widest mb-6">
          Welcome to the event.
        </p>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6" />

        {/* Message */}
        <p className="text-gray-400 text-sm leading-relaxed mb-8">
          Your registration has been received. The organiser will verify your payment and send your digital ticket to your email within 24 hours.
        </p>

        {/* CTA */}
        <button
          onClick={onClose}
          className="w-full bg-sky-500 hover:bg-sky-400 text-black font-black py-4 rounded-2xl transition-all active:scale-95 text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-sky-500/20"
        >
          View All Events
        </button>
      </div>
    </div>
  );
};

export default SuccessModal;